/**
 * Atestados, declarações e laudos no ditado (spec 009, pedido do Leopoldo de
 * 08/10/2026): "selecionar o modelo correto para atestados (afastamento,
 * comparecimento, atestado de consulta com ciclo, sem ciclo, declaração de
 * comparecimento) para gerar o card no ditado e imprimir, laudo
 * autopreenchido, entre outros".
 *
 * O documento é SEMPRE um modelo da clínica (Modelos e Protocolos, tipos
 * Atestado/Declaração e Encaminhamento) — nunca texto escrito pela IA. O
 * modelo de voz diz QUAL modelo ("modelo" = título da lista que ele recebeu
 * no prompt), e aqui se confere e decide, sem IA:
 *
 *  - título EXATO da lista → esse, a não ser que o pedido contradiga
 *    ("sem ciclo" dito e "COM CICLO" escolhido);
 *  - senão, casamento por palavra (com sinônimos: ciclo = cicloplegia =
 *    dilatação; afastamento = repouso…) — "com X" e "sem X" são palavras
 *    diferentes e uma desclassifica a outra;
 *  - empate → o card mostra as opções pra um clique; nada casou → o card
 *    pede o modelo. Os dois são pendência: card sem modelo não imprime.
 *
 * Dias ditados ("atestado de três dias") entram no campo de dias do modelo
 * ("_____ (______________) dias" ou "15 (QUINZE) DIA(S)") com o número por
 * extenso. Tokens {{paciente}}, {{data}}… resolvem na impressão, como no
 * card da ficha; {{ex:…}} (laudo autopreenchido) com os exames do
 * atendimento na hora de imprimir/registrar (`resolveExamTags`).
 *
 * Tudo aqui é puro (sem Firestore), pra testar sem rede.
 */

import type { ClinicalTemplate } from '@/services/firebase/templates';
import { isFillInForm } from '@/utils/notesDefaults';
import { htmlToPlainText } from '@/utils/richText';
import { EMPTY_FIELD, resolveExamTags } from '@/utils/examTags';
import { EXAM_MODULE_BY_ID } from '@/data/encounterCatalog';
import type { EncounterExamsState } from '@/types/encounter';
import type { VoiceEscolha, VoiceGap } from './normalize';

/** Modelo de documento de texto que o ditado pode usar. */
export interface VoiceTextTemplate {
  id: string;
  title: string;
  type: ClinicalTemplate['type'];
  content: string;
}

/** Onde o documento cai na ficha/no histórico — o mesmo do "Documento de Texto". */
export type TextDocDestino = 'atestado' | 'declaracao' | 'encaminhamento';
export type TextDocSubtipo = 'DECLARACAO' | 'LAUDO' | 'ENCAMINHAMENTO';

/** O card "Atestado / laudo" do ditado. */
export interface TextDocDraft {
  kind: 'atestadoLaudo';
  label: string;
  gaps: VoiceGap[];
  /** Como o pedido foi dito ("atestado de consulta com ciclo"). */
  pedido: string;
  /** Modelo escolhido. `null` = ainda não (empate, nada casou ou card vazio). */
  modelo: { id: string; title: string; type: ClinicalTemplate['type'] } | null;
  /** Empate: os modelos pra escolher com um clique no card (ficam depois que o Jev escolhe). */
  opcoes?: Array<{ id: string; title: string }>;
  /** Modelo escolhido sozinho (o Jev) — empate ou pedido sem título igual. */
  escolha?: VoiceEscolha;
  /** Texto do documento (o do modelo, com os dias postos). Tokens {{…}} ficam. */
  texto: string;
  /** Dias ditados (afastamento / repouso). */
  dias?: number;
  /** O médico mexeu no texto ("Editar"): ditar dias de novo não reescreve o modelo. */
  editado?: boolean;
  separado?: boolean;
}

/** Rótulo do card vazio / sem modelo. */
export const TEXT_DOC_EMPTY_LABEL = 'Atestado / laudo';

// ─── Quais modelos entram ───

/**
 * Atestado/Declaração (CERTIFICATE) e Encaminhamento (REFERRAL), só os de
 * TEXTO: modelo com JSON (lista de exames, nota) ou formulário por preencher
 * (`[NOME_PACIENTE]`, `[AXIAL_OD]`) não vira documento ditado.
 */
export function isVoiceTextTemplate(t: Pick<ClinicalTemplate, 'id' | 'type' | 'title' | 'content'>): boolean {
  if (!t.id || !t.title?.trim()) return false;
  if (t.type !== 'CERTIFICATE' && t.type !== 'REFERRAL') return false;
  const c = (t.content ?? '').trim();
  if (!c || c.startsWith('{')) return false;
  return !isFillInForm(htmlToPlainText(c));
}

function semAcento(s: string): string {
  return (s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/**
 * Destino pelo TÍTULO (o tipo só desempata): "atestado" → Atestado;
 * "declaração" → Declaração; "encaminhamento" → Encaminhamento; o resto
 * (laudo, mapeamento, retinografia, termo, orientação…) → Laudo. Diferente
 * do "Documento de Texto" da ficha, que manda CERTIFICATE sem palavra-chave
 * pra Atestado: no histórico, um mapeamento de retina é laudo.
 */
export function textDocDestino(t: { title: string; type: ClinicalTemplate['type'] }): {
  destino: TextDocDestino;
  subtipo?: TextDocSubtipo;
} {
  const title = semAcento(t.title);
  if (title.includes('atestad')) return { destino: 'atestado' };
  if (title.includes('declar')) return { destino: 'declaracao', subtipo: 'DECLARACAO' };
  if (title.includes('encaminh')) return { destino: 'encaminhamento', subtipo: 'ENCAMINHAMENTO' };
  if (t.type === 'REFERRAL' && !title.includes('laudo')) return { destino: 'encaminhamento', subtipo: 'ENCAMINHAMENTO' };
  return { destino: 'declaracao', subtipo: 'LAUDO' };
}

/** "Atestado" / "Declaração" / "Laudo" / "Encaminhamento" — rótulo curto do card. */
export function textDocKindLabel(d: Pick<TextDocDraft, 'modelo'>): string {
  if (!d.modelo) return TEXT_DOC_EMPTY_LABEL;
  const { destino, subtipo } = textDocDestino(d.modelo);
  if (destino === 'atestado') return 'Atestado';
  return subtipo === 'DECLARACAO' ? 'Declaração' : subtipo === 'ENCAMINHAMENTO' ? 'Encaminhamento' : 'Laudo';
}

/** Card da ficha que recebe o documento nos botões Preencher/Substituir. */
export function textDocTargetLabel(d: Pick<TextDocDraft, 'modelo'>): string {
  if (!d.modelo) return 'Documento de Texto';
  const { destino } = textDocDestino(d.modelo);
  return destino === 'atestado' ? 'Atestado' : destino === 'encaminhamento' ? 'Encaminhamento' : 'Laudo / Declaração';
}

// ─── Casamento do pedido com o modelo ───

const IGNORAR = new Set([
  'de', 'do', 'da', 'dos', 'das', 'o', 'a', 'os', 'as', 'um', 'uma', 'para', 'pra', 'pro', 'por', 'e', 'em', 'no',
  'na', 'nos', 'nas', 'ao', 'aos', 'que', 'se', 'eu', 'vou', 'te', 'lhe', 'faz', 'faca', 'fazer', 'dar', 'me',
  'seu', 'sua', 'esse', 'essa', 'este', 'esta', 'ja', 'agora', 'aqui', 'hoje', 'dia', 'dias', 'paciente',
  'senhor', 'senhora', 'imprime', 'imprimir', 'pode', 'gerar', 'gera', 'preparar', 'prepara', 'modelo',
  'documento', 'semana', 'semanas', 'mes', 'meses',
]);

const UNIDADES = ['zero', 'um', 'dois', 'três', 'quatro', 'cinco', 'seis', 'sete', 'oito', 'nove', 'dez',
  'onze', 'doze', 'treze', 'quatorze', 'quinze', 'dezesseis', 'dezessete', 'dezoito', 'dezenove'];
const DEZENAS = ['', '', 'vinte', 'trinta', 'quarenta', 'cinquenta', 'sessenta', 'setenta', 'oitenta', 'noventa'];
const CENTENAS = ['', 'cento', 'duzentos', 'trezentos', 'quatrocentos', 'quinhentos', 'seiscentos', 'setecentos',
  'oitocentos', 'novecentos'];

/** Número falado por extenso, sem acento — não é palavra de modelo. */
const NUMERO_FALADO = new Set(
  [...UNIDADES, ...DEZENAS, ...CENTENAS, 'uma', 'duas', 'cem', 'catorze'].filter(Boolean).map(semAcento),
);

/** Sinônimos → uma palavra só. A chave é a palavra sem acento. */
const CANONICA: Array<[RegExp, string]> = [
  [/^(ciclo|ciclos|ciclado|ciclada|cicloplegia|ciclopegia|cicloplegico|cicloplegica|dilata\w*|midriase|midriatico)$/, 'ciclo'],
  [/^(afastamento|afastar|afastado|afastada|repouso|licenca)$/, 'afastamento'],
  [/^(comparecimento|compareceu|comparecer|presenca|presente)$/, 'comparecimento'],
  [/^(atestado|atestados|atestar|atesto)$/, 'atestado'],
  [/^(declaracao|declaracoes|declarar|declaro)$/, 'declaracao'],
  [/^(laudo|laudos)$/, 'laudo'],
  [/^(autopreenchido|autopreenchida|automatico|automatica)$/, 'autopreenchido'],
  [/^(acompanhante|acompanhantes|acompanhou|acompanhar)$/, 'acompanhante'],
  [/^(encaminhamento|encaminhar|encaminho|encaminhada|encaminhado)$/, 'encaminhamento'],
  [/^(aptidao|apto|apta)$/, 'aptidao'],
  [/^(operatorio|operatoria|operado|operada|cirurgia|cirurgico|cirurgica)$/, 'operatorio'],
  [/^(pterigio|pterigeo)$/, 'pterigio'],
  [/^(consulta|consultas)$/, 'consulta'],
];

function canonica(w: string): string {
  for (const [re, c] of CANONICA) if (re.test(w)) return c;
  return w;
}

/**
 * Palavras do texto, já canônicas. "com X" / "sem X" viram UMA palavra
 * ("com:ciclo", "sem:ciclo") — é a diferença entre os dois atestados de
 * consulta, e não pode virar empate.
 */
export function pedidoTokens(texto: string): string[] {
  const limpo = semAcento(texto)
    .replace(/auto[\s-]+preenchid\w*/g, 'autopreenchido')
    .replace(/\bpos[\s-]+op\w*/g, 'pos operatorio');
  const palavras = limpo.split(/[^a-z0-9]+/).filter(Boolean);
  const out: string[] = [];
  for (let i = 0; i < palavras.length; i += 1) {
    const w = palavras[i];
    if ((w === 'com' || w === 'sem') && i + 1 < palavras.length) {
      // "sem dilatar", "com a dilatação": pula artigo.
      let j = i + 1;
      while (j < palavras.length && IGNORAR.has(palavras[j])) j += 1;
      if (j < palavras.length) {
        out.push(`${w}:${canonica(palavras[j])}`);
        i = j;
        continue;
      }
    }
    if (w === 'com' || w === 'sem' || IGNORAR.has(w)) continue;
    // Número solto ("três", "3") é dia, não modelo.
    if (/^\d+$/.test(w) || NUMERO_FALADO.has(w)) continue;
    out.push(canonica(w));
  }
  return [...new Set(out)];
}

/** Palavras parecidas: igual, uma é começo da outra, ou mesmo radical de 5 letras. */
function mesmaPalavra(a: string, b: string): boolean {
  if (a === b) return true;
  if (a.includes(':') || b.includes(':')) return false;
  if (a.length < 4 || b.length < 4) return false;
  if (a.startsWith(b) || b.startsWith(a)) return true;
  return a.length >= 5 && b.length >= 5 && a.slice(0, 5) === b.slice(0, 5);
}

interface Placar {
  acertos: number;
  conflitos: number;
  /** Todas as palavras do título foram ditas. */
  completo: boolean;
}

/** O título contra o que foi dito. "com:X" casa "com:X" ou "X"; "sem:X" dito desclassifica "com:X" (e vice-versa). */
function placar(tituloTokens: string[], ditos: string[]): Placar {
  let acertos = 0;
  let conflitos = 0;
  for (const t of tituloTokens) {
    const [prefixo, base] = t.includes(':') ? t.split(':') : ['', t];
    if (prefixo) {
      const oposto = `${prefixo === 'com' ? 'sem' : 'com'}:${base}`;
      if (ditos.includes(t) || (prefixo === 'com' && ditos.some((d) => mesmaPalavra(d, base)))) acertos += 1;
      else if (ditos.includes(oposto)) conflitos += 1;
      continue;
    }
    if (ditos.some((d) => mesmaPalavra(d, t) || d === `com:${t}`)) acertos += 1;
  }
  return { acertos, conflitos, completo: acertos === tituloTokens.length && tituloTokens.length > 0 };
}

export interface TextTemplatePick {
  modelo: VoiceTextTemplate | null;
  /** Empate no topo (só quando `modelo` é null). */
  opcoes: VoiceTextTemplate[];
}

/** Máximo de opções num empate — mais que isso o médico escolhe na lista. */
const MAX_OPCOES = 8;

const tituloNorm = (s: string) => semAcento(s).replace(/\s+/g, ' ').trim();

/**
 * Qual modelo da clínica o médico pediu. `modelo` = o que o modelo de voz
 * escolheu (título da lista ou o pedido); `pedido` = como foi dito.
 */
export function pickTextTemplate(
  templates: VoiceTextTemplate[],
  heard: { modelo: string; pedido?: string; dias?: number | null },
): TextTemplatePick {
  if (templates.length === 0) return { modelo: null, opcoes: [] };
  const ditosPedido = pedidoTokens((heard.pedido ?? '').trim());
  const exato = templates.find((t) => tituloNorm(t.title) === tituloNorm(heard.modelo));
  // Título exato da lista: vale, a não ser que o PEDIDO o contradiga.
  if (exato && placar(pedidoTokens(exato.title), ditosPedido).conflitos === 0) return { modelo: exato, opcoes: [] };

  // Contradito, só o pedido decide; senão o "modelo" do modelo de voz soma.
  const ditos = exato ? ditosPedido : [...new Set([...ditosPedido, ...pedidoTokens(heard.modelo)])];
  if (ditos.length === 0) return { modelo: null, opcoes: [] };
  const pontuados = templates
    .map((t) => {
      const p = placar(pedidoTokens(t.title), ditos);
      return { t, p, pontos: p.acertos - 3 * p.conflitos };
    })
    .filter((x) => x.pontos > 0);
  if (pontuados.length === 0) return { modelo: null, opcoes: [] };
  const topo = Math.max(...pontuados.map((x) => x.pontos));
  let empatados = pontuados.filter((x) => x.pontos === topo);
  // Desempate 1: o único título dito INTEIRO ("laudo oftalmológico" ≠ "laudo
  // oftalmológico simples").
  if (empatados.length > 1) {
    const completos = empatados.filter((x) => x.p.completo);
    if (completos.length === 1) empatados = completos;
  }
  // Desempate 2: dias ditos e só um dos empatados tem onde pô-los — o de
  // dias EM BRANCO antes do de dias fixos ("atestado de três dias" é o de
  // afastamento, não o pós-pterígio de 15).
  if (empatados.length > 1 && heard.dias) {
    const comDias = empatados.filter((x) => hasDiasSlot(x.t.content));
    const emBranco = comDias.filter((x) => isDiasBlank(x.t.content));
    if (comDias.length === 1) empatados = comDias;
    else if (emBranco.length === 1) empatados = emBranco;
  }
  if (empatados.length === 1) return { modelo: empatados[0].t, opcoes: [] };
  return { modelo: null, opcoes: empatados.slice(0, MAX_OPCOES).map((x) => x.t) };
}

// ─── Dias ───

/** 0–999 por extenso, pt-BR ("15" → "quinze", "21" → "vinte e um"). */
export function numeroPorExtenso(n: number): string {
  if (!Number.isInteger(n) || n < 0 || n > 999) return String(n);
  if (n < 20) return UNIDADES[n];
  if (n === 100) return 'cem';
  if (n < 100) {
    const d = Math.floor(n / 10);
    const u = n % 10;
    return u ? `${DEZENAS[d]} e ${UNIDADES[u]}` : DEZENAS[d];
  }
  const c = Math.floor(n / 100);
  const resto = n % 100;
  return resto ? `${CENTENAS[c]} e ${numeroPorExtenso(resto)}` : CENTENAS[c];
}

/** "3" / "três" / "dez dias" / "uma semana" → número de dias (1–365). */
export function parseDias(raw: unknown): number | undefined {
  const ok = (n: number) => (Number.isInteger(n) && n > 0 && n <= 365 ? n : undefined);
  if (typeof raw === 'number') return ok(raw);
  if (typeof raw !== 'string') return undefined;
  const t = semAcento(raw).trim();
  // "uma semana" = 7, "duas semanas" = 14, "um mês" = 30.
  const fator = /\bsemanas?\b/.test(t) ? 7 : /\b(mes|meses)\b/.test(t) ? 30 : 1;
  const num = t.match(/\d{1,3}/);
  if (num) return ok(Number(num[0]) * fator);
  let total = 0;
  for (const w of t.split(/[^a-z]+/).filter(Boolean)) {
    const u = UNIDADES.findIndex((x) => semAcento(x) === w);
    const d = DEZENAS.findIndex((x) => x !== '' && x === w);
    if (w === 'uma') total += 1;
    else if (w === 'duas') total += 2;
    else if (w === 'catorze') total += 14;
    else if (u >= 0) total += u;
    else if (d >= 0) total += d * 10;
  }
  if (total === 0 && fator > 1) total = 1;
  return ok(total * fator);
}

/**
 * Campo de dias do modelo: "_____ (______________) dias" (em branco) ou
 * "15 (QUINZE) DIA(S)" (fixo). Número fixo só com o extenso entre
 * parênteses — "por 7 dias" numa posologia não é campo de atestado. O
 * espaço pode vir como &nbsp; nos modelos feitos no editor rico.
 */
const DIAS_EXTENSO =
  /(_{2,}|\b\d{1,3})((?:\s|&nbsp;)*)\(((?:\s|&nbsp;)*)(_{2,}|[A-Za-zÀ-ÿ][A-Za-zÀ-ÿ ]*?)((?:\s|&nbsp;)*)\)((?:\s|&nbsp;)*)(dia\(s\)|dias?\b)/i;
const DIAS_BRANCO = /(_{2,})((?:\s|&nbsp;)+)(dia\(s\)|dias?\b)/i;

export function hasDiasSlot(content: string): boolean {
  return DIAS_EXTENSO.test(content ?? '') || DIAS_BRANCO.test(content ?? '');
}

/** O campo de dias está em branco (modelo de afastamento sem os dias). */
export function isDiasBlank(content: string): boolean {
  const m = (content ?? '').match(DIAS_EXTENSO);
  if (m) return m[1].startsWith('_');
  return DIAS_BRANCO.test(content ?? '');
}

const ehMaiusculo = (s: string) => /[A-ZÀ-Ý]/.test(s) && s === s.toUpperCase();

/** Põe os dias no campo de dias do modelo. Sem campo, devolve igual (`aplicado: false`). */
export function applyDias(content: string, dias: number): { texto: string; aplicado: boolean } {
  const palavra = (orig: string) => {
    if (dias !== 1 || /\(s\)/i.test(orig)) return orig;
    return ehMaiusculo(orig) ? 'DIA' : 'dia';
  };
  const espaco = (s: string) => (s ? s : ' ');
  const m = content.match(DIAS_EXTENSO);
  if (m) {
    const [inteiro, num, esp1, , ext, , esp3, pal] = m;
    const maiusculo = ext.startsWith('_') ? ehMaiusculo(pal) : ehMaiusculo(ext);
    const extenso = numeroPorExtenso(dias);
    // "15 (QUINZE)" com dois dígitos: segue o padrão do modelo ("07 (SETE)").
    const numero = /^\d{2}$/.test(num) ? String(dias).padStart(2, '0') : String(dias);
    const novo = `${numero}${espaco(esp1)}(${maiusculo ? extenso.toUpperCase() : extenso})${espaco(esp3)}${palavra(pal)}`;
    return { texto: content.replace(inteiro, novo), aplicado: true };
  }
  const b = content.match(DIAS_BRANCO);
  if (b) {
    const [inteiro, , esp, pal] = b;
    const extenso = numeroPorExtenso(dias);
    const novo = `${dias} (${ehMaiusculo(pal) ? extenso.toUpperCase() : extenso})${esp}${palavra(pal)}`;
    return { texto: content.replace(inteiro, novo), aplicado: true };
  }
  return { texto: content, aplicado: false };
}

/** O texto tem os dias ditos no campo de dias? */
function diasNoTexto(texto: string, dias: number): boolean {
  const m = texto.match(DIAS_EXTENSO);
  return !!m && Number(m[1]) === dias;
}

// ─── O card ───

/** Pendências: modelo não escolhido, dias em branco, dias ditos que não entraram. */
export function textDocGaps(d: Omit<TextDocDraft, 'gaps' | 'label'>): VoiceGap[] {
  if (!d.modelo) {
    // Card vazio (pré-ativado) não é pendência: só não imprime.
    if (!d.pedido.trim() && !d.opcoes?.length) return [];
    return [{
      field: 'modelo',
      label: `Modelo do documento${d.pedido.trim() ? ` "${d.pedido.trim()}"` : ''}`,
      heard: '',
      issue: d.opcoes && d.opcoes.length > 0 ? 'multi_match' : 'no_match',
    }];
  }
  if (isDiasBlank(d.texto)) return [{ field: 'dias', label: 'Dias de afastamento', heard: '', issue: 'missing' }];
  if (d.dias && !d.editado && !diasNoTexto(d.texto, d.dias)) {
    return [{ field: 'dias', label: `${d.dias} dia(s) ditos — este modelo não tem campo de dias`, heard: '', issue: 'not_applied' }];
  }
  return [];
}

function finalize(d: Omit<TextDocDraft, 'gaps' | 'label'>): TextDocDraft {
  return { ...d, label: d.modelo?.title.trim() || TEXT_DOC_EMPTY_LABEL, gaps: textDocGaps(d) };
}

/** Card vazio ("Aguardando ditado"): o médico ainda pode escolher o modelo na mão. */
export function emptyTextDocDraft(): TextDocDraft {
  return finalize({ kind: 'atestadoLaudo', pedido: '', modelo: null, texto: '' });
}

/** Texto do modelo com os dias (quando há). */
function textoDoModelo(t: VoiceTextTemplate, dias?: number): string {
  return dias ? applyDias(t.content, dias).texto : t.content;
}

/** Monta o card de UM pedido de documento. */
export function buildTextDocDraft(
  templates: VoiceTextTemplate[],
  heard: { modelo: string; pedido?: string; dias?: unknown },
): TextDocDraft {
  const dias = parseDias(heard.dias);
  const pedido = (heard.pedido ?? '').trim() || heard.modelo.trim();
  const { modelo, opcoes } = pickTextTemplate(templates, { modelo: heard.modelo, pedido, dias });
  return finalize({
    kind: 'atestadoLaudo',
    pedido,
    modelo: modelo ? { id: modelo.id, title: modelo.title, type: modelo.type } : null,
    ...(opcoes.length > 0 ? { opcoes: opcoes.map((o) => ({ id: o.id, title: o.title })) } : {}),
    texto: modelo ? textoDoModelo(modelo, dias) : '',
    ...(dias ? { dias } : {}),
  });
}

/** Escolha do modelo no card (empate, troca ou card vazio). Os dias ditos vêm junto. */
export function chooseTextTemplate(d: TextDocDraft, t: VoiceTextTemplate): TextDocDraft {
  const { opcoes: _opcoes, escolha: _escolha, editado: _editado, gaps: _gaps, label: _label, ...resto } = d;
  return finalize({ ...resto, modelo: { id: t.id, title: t.title, type: t.type }, texto: textoDoModelo(t, d.dias) });
}

/**
 * Dias no card (campo do card ou ditado de novo). Com o modelo à mão e o
 * texto não editado, refaz do modelo; senão troca o número no próprio texto.
 */
export function setTextDocDias(d: TextDocDraft, dias: number | undefined, template?: VoiceTextTemplate): TextDocDraft {
  const { dias: _dias, gaps: _gaps, label: _label, ...resto } = d;
  const doModelo = !!template && !d.editado;
  const base = doModelo ? template!.content : d.texto;
  const texto = dias ? applyDias(base, dias).texto : base;
  return finalize({ ...resto, ...(dias ? { dias } : {}), texto });
}

/** Texto editado à mão ("Editar"). */
export function setTextDocTexto(d: TextDocDraft, texto: string): TextDocDraft {
  const { gaps: _gaps, label: _label, ...resto } = d;
  return finalize({ ...resto, texto, editado: true });
}

/**
 * Ditado seguinte pro MESMO documento (mesmo modelo): os dias novos trocam
 * os antigos. `null` = nada mudou (duplicata).
 */
export function mergeTextDocDrafts(existing: TextDocDraft, incoming: TextDocDraft, template?: VoiceTextTemplate): TextDocDraft | null {
  if (!incoming.dias || incoming.dias === existing.dias) return null;
  return setTextDocDias(existing, incoming.dias, template);
}

// ─── Exames do atendimento (laudo autopreenchido) ───

const EX_TOKEN = /\{\{ex:([a-z0-9-]+)(?::[a-zA-Z0-9_]+)?(?::(?:OD|OE|od|oe))?\}\}/g;

/** Módulos citados no texto ({{ex:…}}) que ainda estão sem valor no atendimento. */
export function pendingExamTags(texto: string, state: EncounterExamsState): string[] {
  const vazios: string[] = [];
  for (const m of (texto ?? '').matchAll(EX_TOKEN)) {
    if (resolveExamTags(m[0], state) !== EMPTY_FIELD) continue;
    const nome = EXAM_MODULE_BY_ID[m[1]]?.label ?? m[1];
    if (!vazios.includes(nome)) vazios.push(nome);
  }
  return vazios;
}

// ─── Prompt ───

/** Lista dos títulos pro modelo de voz escolher (Ao vivo e gravação). */
export function textTemplatesPromptList(templates: Array<Pick<VoiceTextTemplate, 'title'>>): string {
  const vistos = new Set<string>();
  return templates
    .map((t) => t.title.replace(/\s+/g, ' ').trim())
    .filter((t) => {
      const k = t.toLowerCase();
      if (!t || vistos.has(k)) return false;
      vistos.add(k);
      return true;
    })
    .map((t) => `- ${t}`)
    .join('\n');
}
